import { useMemo, useRef } from 'react';
import { Perspective } from '@feature/event-detail/types';
import { useGetEventPerspectivesQuery, useGetPostMetadataQuery, PostResponseSchema } from '@/api/client';

interface UseEventPerspectivesParams {
  eventId: string;
}

interface UseEventPerspectivesResult {
  perspectives: Perspective[];
  isLoading: boolean;
  error: any;
}

export function useEventPerspectives({
  eventId,
}: UseEventPerspectivesParams): UseEventPerspectivesResult {
  const { data, isLoading, error } = useGetEventPerspectivesQuery({
    eventId: Number(eventId),
    page: 1,
    size: 50,
  });

  const postIds = useMemo(() => {
    if (!data?.items) return '';
    return data.items.map(post => post.id).join(',');
  }, [data?.items]);

  const { data: metadata, isLoading: isMetadataLoading } = useGetPostMetadataQuery(postIds, {
    skip: !postIds,
    pollingInterval: 30000,
  });

  const perspectivesRef = useRef<Perspective[]>([]);

  const perspectives: Perspective[] = useMemo(() => {
    if (!data?.items) return [];
    // Don't return perspectives until metadata is loaded - otherwise stream status will be wrong
    // isMetadataLoading is only true on initial fetch, not during polling refetches
    if (postIds && isMetadataLoading) return [];

    const newPerspectives = data.items.map((post: PostResponseSchema): Perspective => {
      const isLivestream = post.post_type === 'livestream';
      return {
        id: post.id.toString(),
        username: post.username,
        viewerCount: metadata?.[post.id]?.current_viewer_count ?? 0,
        thumbnailUrl: post.photo_url_compressed || undefined,
        timestamp: post.timestamp,
        postId: post.id.toString(),
        livestreamId: post.livestream_id || '',
        chatChannelId: post.chat_channel_id || '',
        userId: post.user_id,
        // For livestreams, use stream status from metadata; for recorded posts, it's null
        streamStatus: isLivestream
          ? metadata?.[post.id]?.stream_status || 'ended'
          : null,
        m3u8Url: post.m3u8_url || undefined,
        // Recorded post fields
        postType: isLivestream ? 'livestream' : 'recorded',
        videoUrl: post.video_url || undefined,
        photoUrl: post.photo_url || undefined,
        processingState: post.processing_state || undefined,
      };
    });

    // Stabilization: reuse existing objects if only viewerCount changed
    const previous = perspectivesRef.current;
    if (previous.length === newPerspectives.length) {
      let hasNonViewerCountChange = false;

      for (let i = 0; i < newPerspectives.length; i++) {
        const prev = previous[i];
        const curr = newPerspectives[i];

        // Check if anything other than viewerCount/streamStatus/processingState changed
        if (
          prev.id !== curr.id ||
          prev.username !== curr.username ||
          prev.thumbnailUrl !== curr.thumbnailUrl ||
          prev.timestamp !== curr.timestamp ||
          prev.livestreamId !== curr.livestreamId ||
          prev.m3u8Url !== curr.m3u8Url ||
          prev.userId !== curr.userId ||
          prev.postType !== curr.postType ||
          prev.videoUrl !== curr.videoUrl ||
          prev.photoUrl !== curr.photoUrl
        ) {
          hasNonViewerCountChange = true;
          break;
        }
      }

      // If only viewer counts or stream status changed, mutate existing objects to maintain reference stability
      if (!hasNonViewerCountChange) {
        const hasMetadataChange = previous.some(
          (prev, i) =>
            prev.viewerCount !== newPerspectives[i].viewerCount ||
            prev.streamStatus !== newPerspectives[i].streamStatus ||
            prev.processingState !== newPerspectives[i].processingState,
        );

        if (hasMetadataChange) {
          // Mutate in place to keep array reference stable
          previous.forEach((prev, i) => {
            prev.viewerCount = newPerspectives[i].viewerCount;
            prev.streamStatus = newPerspectives[i].streamStatus;
            prev.processingState = newPerspectives[i].processingState;
          });
        }

        return previous;
      }
    }

    perspectivesRef.current = newPerspectives;
    return newPerspectives;
  }, [data?.items, metadata, postIds, isMetadataLoading]);

  // Consider loading until both perspectives AND metadata are ready
  const isFullyLoaded = !isLoading && (!postIds || !isMetadataLoading);

  return {
    perspectives,
    isLoading: !isFullyLoaded,
    error,
  };
}
